import { useEffect, useMemo, useState } from 'react'
import { Button, Form, Input, Modal, Select, Space, Table, Typography } from 'antd'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { revertTimeString } from '@/features/reports/schedule/time'
import { useAuthSession } from '@/lib/session'
import { UserTypes } from '@/types/authorization'
import {
  useAgentBuilderDashboardScheduleRecipientsQuery,
  useAgentBuilderDashboardSchedulesQuery,
  useAgentBuilderDeleteDashboardScheduleMutation,
  useAgentBuilderSaveDashboardScheduleMutation,
} from '../hooks'
import type { AgentBuilderDashboardSchedule, DashboardWidget } from '../types'
import {
  buildIncludedWidgetOptions,
  describeIncludedWidgets,
  initialIncludedWidgetIds,
} from '../scheduleWidgets'
import { IncludedWidgetsField } from './IncludedWidgetsField'

type ScheduleFormValues = {
  includedWidgetIds: string[] | null
  schedule: string
  frequency?: string
  hour: string
  minute: string
  period: 'AM' | 'PM'
  timeZoneId?: string
  users: string[]
  userGroups: string[]
  bcc: string[]
  subject?: string
  body?: string
}

const SCHEDULE_OPTIONS = [
  { value: 'D', label: 'Daily' },
  { value: 'W', label: 'Weekly' },
  { value: 'M', label: 'Monthly' },
  { value: 'NOW', label: 'Now' },
]
const WEEKLY_OPTIONS = [
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
  { value: 'Sun', label: 'Sunday' },
]
const MONTHLY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 1).padStart(2, '0')
  return { value, label: value }
})
const MINUTE_OPTIONS = ['00', '15', '30', '45'].map((value) => ({ value, label: value }))
const PERIOD_OPTIONS = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
]
const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Australia/Perth', label: 'Australia/Perth' },
  { value: 'Australia/Eucla', label: 'Australia/Eucla' },
  { value: 'Australia/Darwin', label: 'Australia/Darwin' },
  { value: 'Australia/Adelaide', label: 'Australia/Adelaide' },
  { value: 'Australia/Brisbane', label: 'Australia/Brisbane' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne' },
  { value: 'Australia/Hobart', label: 'Australia/Hobart' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland' },
  { value: 'Pacific/Chatham', label: 'Pacific/Chatham' },
]
const WEEKDAY_TO_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
const INDEX_TO_WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const TIME_ZONE_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()

function splitSchedule(schedule: string) {
  const [type, frequency] = schedule.includes('-') ? schedule.split('-') : [schedule, undefined]
  return { type: type || 'D', frequency }
}

function describeSchedule(schedule: string) {
  const { type, frequency } = splitSchedule(schedule)
  if (type === 'NOW') return 'Now'
  if (type === 'D') return 'Daily'
  if (type === 'W') return `Weekly${frequency ? ` - ${frequency}` : ''}`
  if (type === 'M') return `Monthly${frequency ? ` - day ${frequency}` : ''}`
  return schedule
}

function getBrowserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

function getTimeZoneFormatter(timeZone: string) {
  const key = timeZone || 'UTC'
  const cached = TIME_ZONE_FORMATTER_CACHE.get(key)
  if (cached) return cached
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: key,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  TIME_ZONE_FORMATTER_CACHE.set(key, formatter)
  return formatter
}

function getTimeZoneParts(date: Date, timeZone: string) {
  const parts = getTimeZoneFormatter(timeZone).formatToParts(date)
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  const weekday = byType['weekday'] || INDEX_TO_WEEKDAY[date.getDay()] || 'Mon'
  return {
    year: Number(byType['year']),
    month: Number(byType['month']),
    day: Number(byType['day']),
    hour: Number(byType['hour']),
    minute: Number(byType['minute']),
    weekday,
  }
}

function getTimeZoneOffsetMinutes(date: Date, timeZone: string) {
  const parts = getTimeZoneParts(date, timeZone)
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute)
  return (asUtc - date.getTime()) / 60000
}

function zonedLocalToUtcDate(year: number, month: number, day: number, hour: number, minute: number, timeZone: string) {
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute))
  const firstOffset = getTimeZoneOffsetMinutes(guess, timeZone)
  const firstUtc = new Date(guess.getTime() - firstOffset * 60000)
  const secondOffset = getTimeZoneOffsetMinutes(firstUtc, timeZone)
  return new Date(guess.getTime() - secondOffset * 60000)
}

function parseTime(time: string) {
  const [rawHour, rawMinute] = time.split(':').map((value) => Number(value))
  const hour = rawHour ?? Number.NaN
  const minute = rawMinute ?? Number.NaN
  return {
    hour: Number.isFinite(hour) ? hour : 8,
    minute: Number.isFinite(minute) ? minute : 0,
  }
}

function resolveNextScheduleInstant(schedule: string, scheduleTime: string, timeZone: string) {
  const { type, frequency } = splitSchedule(schedule)
  const now = new Date()
  const zonedNow = getTimeZoneParts(now, timeZone)
  const { hour, minute } = parseTime(scheduleTime)
  let candidateYear = zonedNow.year
  let candidateMonth = zonedNow.month
  let candidateDay = zonedNow.day

  if (type === 'W') {
    const targetDay = WEEKDAY_TO_INDEX[frequency ?? ''] ?? 1
    const currentDay = WEEKDAY_TO_INDEX[zonedNow.weekday] ?? now.getDay()
    candidateDay += (targetDay - currentDay + 7) % 7
  } else if (type === 'M') {
    const targetDay = Number(frequency)
    candidateDay = Number.isFinite(targetDay) ? targetDay : zonedNow.day
  }

  let candidate = zonedLocalToUtcDate(candidateYear, candidateMonth, candidateDay, hour, minute, timeZone)
  const candidateParts = getTimeZoneParts(candidate, timeZone)
  if (candidate <= now || candidateParts.month !== candidateMonth) {
    if (type === 'D') {
      candidateDay += 1
    } else if (type === 'W') {
      candidateDay += 7
    } else if (type === 'M') {
      candidateMonth += 1
      if (candidateMonth > 12) {
        candidateMonth = 1
        candidateYear += 1
      }
    }
    candidate = zonedLocalToUtcDate(candidateYear, candidateMonth, candidateDay, hour, minute, timeZone)
  }

  return candidate
}

function convertScheduleToTimeZone(schedule: AgentBuilderDashboardSchedule, targetTimeZone: string) {
  if (!schedule.timeZoneId || schedule.timeZoneId === targetTimeZone || schedule.schedule === 'NOW') {
    return { schedule: schedule.schedule, scheduleTime: schedule.scheduleTime, timeZoneId: schedule.timeZoneId || targetTimeZone }
  }

  const converted = getTimeZoneParts(resolveNextScheduleInstant(schedule.schedule, schedule.scheduleTime, schedule.timeZoneId), targetTimeZone)
  const { type } = splitSchedule(schedule.schedule)
  const convertedSchedule = type === 'W'
    ? `W-${converted.weekday}`
    : type === 'M'
      ? `M-${converted.day}`
      : schedule.schedule

  return {
    schedule: convertedSchedule,
    scheduleTime: `${String(converted.hour).padStart(2, '0')}:${String(converted.minute).padStart(2, '0')}`,
    timeZoneId: targetTimeZone,
  }
}

function toLocalTimeString(hour: string, minute: string, period: 'AM' | 'PM') {
  let hour24 = Number(hour)
  if (period === 'PM' && hour24 < 12) hour24 += 12
  if (period === 'AM' && hour24 === 12) hour24 = 0
  return `${String(hour24).padStart(2, '0')}:${minute}`
}

type Props = {
  open: boolean
  clientId: string
  dashboardRootId: string | null
  dashboardTitle: string
  widgets: DashboardWidget[]
  onClose: () => void
}

export function AgentBuilderScheduleModal({ open, clientId, dashboardRootId, dashboardTitle, widgets, onClose }: Props) {
  const { addToast } = useToast()
  const authSession = useAuthSession()
  const [form] = Form.useForm<ScheduleFormValues>()
  const [editingSchedule, setEditingSchedule] = useState<AgentBuilderDashboardSchedule | null>(null)
  const schedulesQuery = useAgentBuilderDashboardSchedulesQuery(clientId, dashboardRootId)
  const recipientsQuery = useAgentBuilderDashboardScheduleRecipientsQuery(clientId, open)
  const saveMutation = useAgentBuilderSaveDashboardScheduleMutation(clientId, dashboardRootId)
  const deleteMutation = useAgentBuilderDeleteDashboardScheduleMutation(clientId, dashboardRootId)
  const scheduleType = Form.useWatch('schedule', form)
  const isDiAdmin = authSession?.currentUserTypeId === UserTypes.Admin
  const defaultTimeZone = useMemo(() => getBrowserTimeZone(), [])
  const timezoneOptions = useMemo(() => {
    if (TIMEZONE_OPTIONS.some((option) => option.value === defaultTimeZone)) return TIMEZONE_OPTIONS
    return [{ value: defaultTimeZone, label: defaultTimeZone }, ...TIMEZONE_OPTIONS]
  }, [defaultTimeZone])

  const widgetOptions = useMemo(() => buildIncludedWidgetOptions(widgets), [widgets])

  const recipientOptions = useMemo(() => {
    const all = recipientsQuery.data ?? []
    return {
      users: all.filter((item) => item.type === 1),
      groups: all.filter((item) => item.type === 2),
    }
  }, [recipientsQuery.data])

  useEffect(() => {
    if (!open) return
    // setFieldsValue's partial type drops null, which means "all widgets" here.
    form.setFieldValue('includedWidgetIds', null)
    form.setFieldsValue({
      schedule: 'D',
      hour: '08',
      minute: '00',
      period: 'AM',
      timeZoneId: defaultTimeZone,
      users: [],
      userGroups: [],
      bcc: [],
      subject: `${dashboardTitle} visual data`,
      body: '',
    })
    setEditingSchedule(null)
  }, [dashboardTitle, defaultTimeZone, form, open])

  function startEdit(schedule: AgentBuilderDashboardSchedule) {
    const editableSchedule = isDiAdmin ? schedule : { ...schedule, ...convertScheduleToTimeZone(schedule, defaultTimeZone) }
    const scheduleParts = splitSchedule(editableSchedule.schedule)
    const time = revertTimeString(editableSchedule.scheduleTime)
    setEditingSchedule(schedule)
    form.setFieldValue('includedWidgetIds', schedule.includedWidgetIds == null ? null : initialIncludedWidgetIds(schedule, widgets))
    form.setFieldsValue({
      schedule: scheduleParts.type,
      frequency: scheduleParts.frequency,
      hour: time.hour,
      minute: time.minute,
      period: time.period,
      timeZoneId: editableSchedule.timeZoneId || defaultTimeZone,
      users: schedule.recipients.filter((item) => item.type === 1).map((item) => item.id),
      userGroups: schedule.recipients.filter((item) => item.type === 2).map((item) => item.id),
      bcc: schedule.bcc,
      subject: schedule.subject,
      body: schedule.body,
    })
  }

  async function save() {
    if (!dashboardRootId) return
    const values = await form.validateFields()
    const schedule = values.schedule === 'W' || values.schedule === 'M'
      ? `${values.schedule}-${values.frequency}`
      : values.schedule
    const scheduleTime = schedule === 'NOW'
      ? '00:00'
      : toLocalTimeString(values.hour, values.minute, values.period)
    const timeZoneId = schedule === 'NOW'
      ? undefined
      : isDiAdmin
        ? values.timeZoneId || defaultTimeZone
        : defaultTimeZone
    const recipients = [
      ...(values.users ?? []).map((id) => ({ id, type: 1 })),
      ...(values.userGroups ?? []).map((id) => ({ id, type: 2 })),
    ]

    try {
      await saveMutation.mutateAsync({
        agentBuilderDashboardSchedule: {
          agentBuilderDashboardScheduleId: editingSchedule?.agentBuilderDashboardScheduleId ?? '00000000-0000-0000-0000-000000000000',
          agentBuilderDashboardRootId: dashboardRootId,
          clientId,
          schedule,
          scheduleTime,
          timeZoneId,
          recipients,
          bcc: values.bcc ?? [],
          subject: values.subject ?? '',
          body: values.body ?? '',
          includedWidgetIds: values.includedWidgetIds ?? null,
        },
      })
      addToast('PracIntel schedule saved.', 'success')
      setEditingSchedule(null)
      form.resetFields()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save PracIntel schedule.', 'error')
    }
  }

  async function remove(scheduleId: string) {
    try {
      await deleteMutation.mutateAsync(scheduleId)
      addToast('PracIntel schedule deleted.', 'success')
      if (editingSchedule?.agentBuilderDashboardScheduleId === scheduleId) {
        setEditingSchedule(null)
        form.resetFields()
      }
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to delete PracIntel schedule.', 'error')
    }
  }

  return (
    <Modal
      open={open}
      title="Schedule Visual Data"
      onCancel={onClose}
      width={920}
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
        <Button key="save" type="primary" loading={saveMutation.isPending} onClick={() => { void save() }}>
          {editingSchedule ? 'Save Schedule' : 'Add Schedule'}
        </Button>,
      ]}
    >
      <Space direction="vertical" size={16} className="w-full">
        <Table<AgentBuilderDashboardSchedule>
          rowKey="agentBuilderDashboardScheduleId"
          loading={schedulesQuery.isLoading}
          dataSource={schedulesQuery.data ?? []}
          pagination={false}
          size="small"
          columns={[
            { title: 'Frequency', dataIndex: 'schedule', render: (_, row) => describeSchedule(isDiAdmin ? row.schedule : convertScheduleToTimeZone(row, defaultTimeZone).schedule) },
            { title: 'Time', dataIndex: 'scheduleTime', render: (_, row) => isDiAdmin ? row.scheduleTime : convertScheduleToTimeZone(row, defaultTimeZone).scheduleTime },
            ...(isDiAdmin ? [{ title: 'Timezone', dataIndex: 'timeZoneId', render: (value: string) => value || '-' }] : []),
            { title: 'Subject', dataIndex: 'subject' },
            { title: 'Widgets', key: 'widgets', render: (_, row) => describeIncludedWidgets(row, widgets) },
            {
              title: '',
              key: 'actions',
              width: 96,
              render: (_, row) => (
                <Space>
                  <Button size="small" type="text" icon={<EditOutlined />} aria-label="Edit schedule" onClick={() => startEdit(row)} />
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    aria-label="Delete schedule"
                    loading={deleteMutation.isPending}
                    onClick={() => { void remove(row.agentBuilderDashboardScheduleId) }}
                  />
                </Space>
              ),
            },
          ]}
        />

        <Typography.Title level={5} className="!mb-0">
          {editingSchedule ? 'Edit Schedule' : 'New Schedule'}
        </Typography.Title>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ includedWidgetIds: null, schedule: 'D', hour: '08', minute: '00', period: 'AM', timeZoneId: defaultTimeZone, users: [], userGroups: [], bcc: [] }}
        >
          <Form.Item
            name="includedWidgetIds"
            label="Included Widgets"
            rules={[{
              // null ("all widgets") is always valid, as it was before widgets could be picked.
              // An empty pick is only fixable when there is an exportable widget to tick.
              validator: (_, value: string[] | null) => (value === null || value.length > 0 || widgetOptions.every((o) => o.disabled)
                ? Promise.resolve()
                : Promise.reject(new Error('Select at least one widget.'))),
            }]}
          >
            <IncludedWidgetsField options={widgetOptions} />
          </Form.Item>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12">
            <Form.Item name="schedule" label="Frequency" rules={[{ required: true }]} className="mb-0 xl:col-span-3">
              <Select options={SCHEDULE_OPTIONS} />
            </Form.Item>
            {scheduleType === 'W' ? (
              <Form.Item name="frequency" label="Day of Week" rules={[{ required: true }]} className="mb-0 xl:col-span-3">
                <Select options={WEEKLY_OPTIONS} />
              </Form.Item>
            ) : null}
            {scheduleType === 'M' ? (
              <Form.Item name="frequency" label="Day of Month" rules={[{ required: true }]} className="mb-0 xl:col-span-3">
                <Select options={MONTHLY_OPTIONS} />
              </Form.Item>
            ) : null}
            {scheduleType !== 'NOW' ? (
              <>
                <Form.Item name="hour" label="Hour" rules={[{ required: true }]} className="mb-0 xl:col-span-2">
                  <Select options={HOUR_OPTIONS} />
                </Form.Item>
                <Form.Item name="minute" label="Minute" rules={[{ required: true }]} className="mb-0 xl:col-span-2">
                  <Select options={MINUTE_OPTIONS} />
                </Form.Item>
                <Form.Item name="period" label="Period" rules={[{ required: true }]} className="mb-0 xl:col-span-2">
                  <Select options={PERIOD_OPTIONS} />
                </Form.Item>
                {isDiAdmin ? (
                  <Form.Item name="timeZoneId" label="Timezone" rules={[{ required: true }]} className="mb-0 xl:col-span-3">
                    <Select showSearch optionFilterProp="label" options={timezoneOptions} />
                  </Form.Item>
                ) : null}
              </>
            ) : null}
          </div>

          <Form.Item name="userGroups" label="User Groups">
            <Select
              mode="multiple"
              loading={recipientsQuery.isLoading}
              options={recipientOptions.groups.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          <Form.Item name="users" label="Specific Users" rules={[{ validator: async () => {
            const users = form.getFieldValue('users') ?? []
            const groups = form.getFieldValue('userGroups') ?? []
            if (users.length + groups.length === 0) throw new Error('Select at least one user or user group.')
          } }]}>
            <Select
              mode="multiple"
              loading={recipientsQuery.isLoading}
              options={recipientOptions.users.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          <Form.Item name="bcc" label="Email BCC">
            <Select
              mode="multiple"
              loading={recipientsQuery.isLoading}
              options={recipientOptions.users.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          <Form.Item name="subject" label="Email Subject">
            <Input />
          </Form.Item>
          <Form.Item name="body" label="Email Body">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}

export default AgentBuilderScheduleModal
